import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrentLanguage, setLanguage as setLang, t as translate, languages } from '../lib/i18n';
import { profileAPI } from '../lib/api';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getCurrentLanguage());
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setLanguageState(newLang);
      forceUpdate(n => n + 1); // Force re-render of all consumers
    };
    
    window.addEventListener('languagechange', handleLanguageChange);
    return () => window.removeEventListener('languagechange', handleLanguageChange);
  }, []);

  // Load language preference from server when user is logged in
  const loadLanguagePreference = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await profileAPI.getLanguagePreference();
        const serverLang = response.data?.language;
        if (serverLang && serverLang !== language) {
          setLang(serverLang);
          setLanguageState(serverLang);
          forceUpdate(n => n + 1);
        }
      } catch (error) {
        // Silently fail - user may not be logged in
        if (process.env.NODE_ENV === 'development') {
          console.debug('Could not load language preference:', error);
        }
      }
    }
  }, [language]);

  // Load preference on mount
  useEffect(() => {
    loadLanguagePreference();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const setLanguage = useCallback(async (lang) => {
    setLang(lang);
    setLanguageState(lang);
    forceUpdate(n => n + 1); // Force re-render
    
    // Save to server if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await profileAPI.updateLanguagePreference(lang);
      } catch (error) {
        // Silently fail - preference is still saved locally
        if (process.env.NODE_ENV === 'development') {
          console.debug('Could not save language preference:', error);
        }
      }
    }
  }, []);

  // Create a memoized t function that depends on the current language
  const t = useCallback((key) => translate(key, language), [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages, loadLanguagePreference }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
